import { useEffect, useRef, useState } from "react";
import { Music2, VolumeX } from "lucide-react";

const TARGET_VOLUME = 0.18;
const FADE_MS = 1200;
const CROSSFADE_MS = 2000;
const STORAGE_KEY = "bgm-muted";
const SRC = "/audio/clair-de-lune.ogg";

function fadeTo(audio: HTMLAudioElement, target: number, duration: number, onDone?: () => void) {
  const start = audio.volume;
  const startTime = performance.now();
  const step = (now: number) => {
    const t = Math.min(1, (now - startTime) / duration);
    audio.volume = start + (target - start) * t;
    if (t < 1) requestAnimationFrame(step);
    else onDone?.();
  };
  requestAnimationFrame(step);
}

export function BackgroundMusic() {
  const aRef = useRef<HTMLAudioElement | null>(null);
  const bRef = useRef<HTMLAudioElement | null>(null);
  const activeRef = useRef<"a" | "b">("a");
  const startedRef = useRef(false);
  const crossfadeTimerRef = useRef<number | null>(null);

  const [playing, setPlaying] = useState(false);
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    const muted = typeof window !== "undefined" && localStorage.getItem(STORAGE_KEY) === "1";

    const make = () => {
      const a = new Audio();
      a.preload = "none";
      a.src = SRC;
      a.volume = 0;
      return a;
    };
    aRef.current = make();
    bRef.current = make();

    const scheduleCrossfade = () => {
      if (crossfadeTimerRef.current) window.clearTimeout(crossfadeTimerRef.current);
      const cur = activeRef.current === "a" ? aRef.current! : bRef.current!;
      const onMeta = () => {
        const remaining = (cur.duration - cur.currentTime) * 1000 - CROSSFADE_MS;
        if (!isFinite(remaining) || remaining < 0) return;
        crossfadeTimerRef.current = window.setTimeout(() => {
          const next = activeRef.current === "a" ? bRef.current! : aRef.current!;
          next.currentTime = 0;
          next.volume = 0;
          next.play().catch(() => {});
          fadeTo(next, TARGET_VOLUME, CROSSFADE_MS);
          fadeTo(cur, 0, CROSSFADE_MS, () => cur.pause());
          activeRef.current = activeRef.current === "a" ? "b" : "a";
          scheduleCrossfade();
        }, remaining);
      };
      if (cur.readyState >= 1) onMeta();
      else cur.addEventListener("loadedmetadata", onMeta, { once: true });
    };

    const start = () => {
      if (startedRef.current) return;
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
      const cur = aRef.current!;
      cur.play()
        .then(() => {
          startedRef.current = true;
          setPlaying(true);
          setShowHint(false);
          fadeTo(cur, TARGET_VOLUME, FADE_MS);
          scheduleCrossfade();
          remove();
        })
        .catch(() => {});
    };

    const remove = () => {
      window.removeEventListener("pointerdown", start);
      window.removeEventListener("keydown", start);
      window.removeEventListener("touchstart", start);
    };

    if (!muted) {
      window.addEventListener("pointerdown", start);
      window.addEventListener("keydown", start);
      window.addEventListener("touchstart", start);
      const hintTimer = window.setTimeout(() => setShowHint(true), 1500);
      const hideTimer = window.setTimeout(() => setShowHint(false), 6000);
      return () => {
        window.clearTimeout(hintTimer);
        window.clearTimeout(hideTimer);
        remove();
        if (crossfadeTimerRef.current) window.clearTimeout(crossfadeTimerRef.current);
        aRef.current?.pause();
        bRef.current?.pause();
      };
    }

    return () => {
      remove();
      if (crossfadeTimerRef.current) window.clearTimeout(crossfadeTimerRef.current);
      aRef.current?.pause();
      bRef.current?.pause();
    };
  }, []);

  const toggle = () => {
    setShowHint(false);
    const cur = activeRef.current === "a" ? aRef.current : bRef.current;
    if (!cur) return;
    if (playing) {
      fadeTo(cur, 0, FADE_MS, () => cur.pause());
      if (crossfadeTimerRef.current) window.clearTimeout(crossfadeTimerRef.current);
      setPlaying(false);
      localStorage.setItem(STORAGE_KEY, "1");
    } else {
      localStorage.removeItem(STORAGE_KEY);
      cur.play()
        .then(() => {
          setPlaying(true);
          fadeTo(cur, TARGET_VOLUME, FADE_MS);
        })
        .catch(() => {});
    }
  };

  return (
    <div className="fixed right-3 bottom-24 sm:bottom-4 z-50 flex items-center gap-2">
      {showHint && !playing && (
        <span className="hidden sm:inline-block animate-fade-in rounded-full bg-background/90 px-3 py-1.5 text-xs text-muted-foreground shadow-md backdrop-blur-md border border-border/60">
          Click to play background music
        </span>
      )}
      <button
        type="button"
        onClick={toggle}
        aria-pressed={playing}
        aria-label={playing ? "Turn off background music" : "Turn on background music"}
        className="flex h-9 w-9 sm:h-11 sm:w-11 items-center justify-center rounded-full border border-border/60 bg-background/80 text-foreground shadow-lg backdrop-blur-md transition-all hover:scale-105 hover:bg-background motion-reduce:hover:scale-100"
      >
        {playing ? (
          <Music2 className="h-4 w-4 sm:h-5 sm:w-5 motion-safe:animate-pulse" />
        ) : (
          <VolumeX className="h-4 w-4 sm:h-5 sm:w-5 opacity-70" />
        )}
      </button>
    </div>
  );
}
